// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stepper,
  Step,
  Typography,
  StepLabel,
  CircularProgress,
} from '@mui/material';
import { IconButton } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { type FC, useCallback } from 'react';
import { AccountsStep } from './accounts-step';
import { ClientHandoffScreen } from './client-handoff-screen';
import { AdvisoryTypeStep } from './advisory-type-step';
import { CommissionStep } from './commission-step';
import { ReviewStep } from './review-step';
import {
  useProfilingData,
  withProfiling,
} from '@shared/advisory/client-profile/providers';
import { selectClientId } from '@shared/data-access/slices/clients/clients-selectors';
import { useSelector } from 'react-redux';

export const ProfilingModal: FC = () => {
  const clientId = useSelector(selectClientId);

  const {
    activeStep,
    step,
    steps,
    open,
    onOpen,
    onClose,
    onCloseDialog,
    onPrevious,
    onNext,
    onSave,
    isLoading,
    hidden,
  } = useProfilingData();

  const renderStep = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <AdvisoryTypeStep />;
      case 1:
        return <AccountsStep />;
      case 2:
        return <CommissionStep />;
      case 3:
        return <ReviewStep />;
      case 4:
        return <ClientHandoffScreen />;
      default:
        return null;
    }
  }, [activeStep]);

  return (
    <>
      {!hidden && (
        <Button
          onClick={onOpen}
          variant="filled"
          color="secondary"
          data-testid="activate-button"
          disabled={clientId === null}
        >
          <Translation i18nKey="client-profile.activate" ns="components" />
        </Button>
      )}

      <Dialog
        open={open}
        onClose={onCloseDialog}
        PaperProps={{
          className: '!max-w-4xl !max-h-[85vh] !w-full',
        }}
        data-testid="client-profile-modal"
        disableEscapeKeyDown
      >
        <DialogTitle
          component="div"
          className="relative !m-0 !px-6 !pb-5 !pt-6"
          data-testid="profiling-modal-title"
        >
          <div className="absolute right-4 top-4">
            <IconButton
              icon="Close"
              onClick={onClose}
              data-testid="close-button"
              variant="icon"
              color="disabled"
              size="md"
            />
          </div>

          <Typography
            variant="h5"
            component="div"
            className="pr-10"
            data-testid="modal-title"
          >
            <div className="flex flex-col gap-1">
              <span className="block">
                <Translation
                  i18nKey="client-profile.activate-modal-title"
                  ns="components"
                />
              </span>
              <span className="block">
                <Translation
                  i18nKey="client-profile.activate-modal-service-provider"
                  ns="components"
                />
              </span>
            </div>
          </Typography>
        </DialogTitle>

        <DialogContent className="!px-6 !pt-0">
          <div className="border-b border-border-sep pb-6">
            <Stepper activeStep={activeStep}>
              {steps.map(({ label, id }) => (
                <Step key={id} data-testid={`step-${id}`}>
                  <StepLabel
                    StepIconProps={{
                      className: '!h-6 !w-6',
                    }}
                    aria-label={label}
                  ></StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>

          <div className="mt-6 overflow-auto px-[8px]" data-testid="step-content">
            {renderStep()}
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5 }} className="!gap-4">
          {step.canBack && (
            <Button
              variant="outlined"
              color="secondary"
              className="!basis-1/2"
              onClick={onPrevious}
              disabled={activeStep === 0 || isLoading}
              data-testid="previous-button"
            >
              <Translation ns="common" i18nKey="common.previous" />
            </Button>
          )}

          {activeStep === steps.length - 1 ? (
            <Button
              color="secondary"
              className={step.canBack ? '!basis-1/2' : '!ml-auto !min-w-[12rem]'}
              onClick={onSave}
              data-testid="save-button"
            >
              <Translation ns="common" i18nKey="common.close" />
            </Button>
          ) : (
            <Button
              color="secondary"
              className={step.canBack ? '!basis-1/2' : '!ml-auto !min-w-[12rem]'}
              onClick={onNext}
              disabled={!steps[activeStep].valid || isLoading}
              data-testid="next-button"
              endIcon={
                isLoading ? (
                  <CircularProgress size={16} color="secondary" />
                ) : undefined
              }
            >
              {isLoading ? (
                <Translation ns="common" i18nKey="common.sending" />
              ) : (
                step?.nextLabel ?? <Translation ns="common" i18nKey="common.next" />
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withProfiling(ProfilingModal);
